"use client";

import { useRef } from "react";
import { Database } from "lucide-react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import {
  EASE_OUT,
  gsap,
  NOT_REDUCED,
  playOnEnter,
  useEnterStagger,
  useGSAP,
} from "@/components/sections/kit/motion";
import { DATA_TYPES, SPECS } from "./data";

const LABEL_BAR =
  "border-b border-line bg-surface-2 px-6 py-[13px] font-jetbrains text-[10.5px] uppercase tracking-[0.12em] text-dim";

/**
 * Data protection — what each data type is, beside the transport/retention
 * spec sheet, closed by the no-selling-data statement whose pink underline
 * sweeps in on scroll. On the neutral wash band.
 */
export default function DataProtectionSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const stmtRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  // stagger 0 — the two cards are one visual row; a stagger reads as
  // misaligned cards mid-flight.
  useEnterStagger(rootRef, "[data-dp-card]", { y: 26, stagger: 0 });
  useEnterStagger(stmtRef, "[data-statement]", { y: 26 });

  // The ink sweep: the statement's pink underline draws left→right once the
  // statement enters. At rest (and under reduced motion) it is fully drawn.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const tween = gsap.from("[data-ink-underline]", {
          backgroundSize: "0% 5px",
          duration: 0.9,
          delay: 0.35,
          ease: EASE_OUT,
          paused: true,
          immediateRender: false,
        });
        return playOnEnter(tween, stmtRef.current);
      });
    },
    { scope: stmtRef },
  );

  return (
    <SectionShell
      id="data"
      band="neutral"
      className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px"
    >
      {/* flex-col: margin collapse against SectionHead's mb otherwise swallows
          the aligner's first corrections and overshoots a later block by a
          whole row (the /integrations lesson). */}
      <div ref={rootRef} className="flex flex-col">
        <SectionHead
          eyebrow="Data protection"
          title="What we keep, and how."
          lead="Per data type — not a blanket claim — plus the transport and retention facts a questionnaire actually asks for. EU-first — visitor IPs anonymised before storage."
        />

        <div data-grid-block className="grid items-start gap-4 lg:grid-cols-[5fr_7fr]">
          {/* Per data type. */}
          <div
            data-dp-card
            className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref"
          >
            <div className={LABEL_BAR}>Data — per type</div>
            <div className="px-6 pb-2 pt-1.5">
              {DATA_TYPES.map((row) => (
                <div key={row.type} className="flex items-start gap-3.5 border-b border-line py-3.5">
                  <span className="mt-px flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full border border-brand-600/20 bg-brand-600/[0.07] text-brand-600">
                    <Database size={12} strokeWidth={2} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                      <span className="text-[13.5px] font-bold">{row.type}</span>
                      <span className="ml-auto whitespace-nowrap font-jetbrains text-[11px] text-brand-600">
                        {row.tag}
                      </span>
                    </div>
                    <p className="mt-1 text-[12.5px] leading-[1.5] text-muted">{row.desc}</p>
                  </div>
                </div>
              ))}
              <p className="py-3.5 font-jetbrains text-[10.5px] text-dim">
                every processor and its country · privacy policy §7
              </p>
            </div>
          </div>

          {/* Encryption & retention — spec sheet. */}
          <div
            data-dp-card
            className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref"
          >
            <div className={LABEL_BAR}>Transport &amp; retention — spec sheet</div>
            <div className="px-6 pb-5 pt-2">
              {SPECS.map((spec) => (
                <div
                  key={spec.k}
                  className="flex items-baseline gap-5 border-b border-line py-3 last:border-b-0 max-sm:flex-col max-sm:gap-1"
                >
                  <span className="w-[150px] flex-none text-[13px] font-semibold">{spec.k}</span>
                  <span className="text-[13px] leading-[1.55] text-muted">
                    {spec.v}
                    {spec.note && (
                      <span className="ml-2 whitespace-nowrap rounded-full border border-data-amber/35 bg-[color-mix(in_srgb,var(--color-data-amber)_12%,white)] px-2 py-0.5 font-jetbrains text-[9.5px] text-data-amber">
                        {spec.note}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The statement moment. */}
        <div ref={stmtRef} data-grid-block className="max-md:mt-12">
          <p
            data-statement
            className="mx-auto max-w-[780px] text-center text-[clamp(24px,2.8vw,32px)] font-extrabold leading-[1.35] tracking-[-0.02em]"
          >
            We{" "}
            <span
              data-ink-underline
              className="bg-[linear-gradient(90deg,var(--color-brand-600),var(--color-brand-500))] bg-no-repeat pb-[3px] [background-position:0_96%] [background-size:100%_5px]"
            >
              do not sell click data
            </span>
            . <span className="text-muted">Not aggregated, not &ldquo;anonymized&rdquo;, not ever.</span>
          </p>
        </div>
      </div>
    </SectionShell>
  );
}
